import { createContext, useContext, useEffect, useState, useCallback } from "react";

const LangCtx = createContext({ lang: "de", setLang: () => {}, hintNeeded: null, dismissHint: () => {} });

const STORAGE_KEY = "g21-lang";
const HINT_DISMISSED_KEY = "g21-lang-hint-dismissed";

const detectBrowserLang = () => {
    if (typeof navigator === "undefined") return "de";
    const raw = (navigator.language || navigator.userLanguage || "de").toLowerCase();
    if (raw.startsWith("de")) return "de";
    return "en"; // anything non-German → suggest English
};

/**
 * Tiny i18n: usage in components
 *   const t = useT();
 *   <h1>{t({ de: 'Hallo Welt', en: 'Hello World' })}</h1>
 *
 * Also auto-detects browser language on first visit and exposes `hintNeeded`
 * so a small banner can suggest switching.
 */
export const LanguageProvider = ({ children }) => {
    const [lang, setLangState] = useState("de");
    const [hintNeeded, setHintNeeded] = useState(null); // 'en' | 'de' | null

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            const dismissed = localStorage.getItem(HINT_DISMISSED_KEY);
            if (stored === "en" || stored === "de") {
                setLangState(stored);
                document.documentElement.lang = stored;
                return;
            }
            // No explicit choice yet – detect browser language
            const browserLang = detectBrowserLang();
            // We default-render in DE; if browser prefers EN, suggest the switch
            if (browserLang !== "de" && !dismissed) {
                setHintNeeded(browserLang);
            }
        } catch {
            /* ignore */
        }
    }, []);

    const setLang = useCallback((l) => {
        setLangState(l);
        try {
            localStorage.setItem(STORAGE_KEY, l);
            localStorage.setItem(HINT_DISMISSED_KEY, "1");
            document.documentElement.lang = l;
        } catch {
            /* ignore */
        }
        setHintNeeded(null);
    }, []);

    const dismissHint = useCallback(() => {
        try {
            localStorage.setItem(HINT_DISMISSED_KEY, "1");
        } catch {
            /* ignore */
        }
        setHintNeeded(null);
    }, []);

    return (
        <LangCtx.Provider value={{ lang, setLang, hintNeeded, dismissHint }}>{children}</LangCtx.Provider>
    );
};

export const useLang = () => useContext(LangCtx);

/**
 * Returns a translator function `t({ de, en })`.
 * Falls back to German if the EN string is missing.
 */
export const useT = () => {
    const { lang } = useContext(LangCtx);
    return (obj) => {
        if (typeof obj === "string") return obj;
        if (!obj) return "";
        return obj[lang] || obj.de || obj.en || "";
    };
};

/**
 * DE | EN switch button. Tiny, fits in the navbar.
 */
export const LanguageToggle = ({ className = "", colorClass = "text-stone-700" }) => {
    const { lang, setLang } = useContext(LangCtx);
    const other = lang === "de" ? "en" : "de";
    return (
        <button
            type="button"
            onClick={() => setLang(other)}
            className={`inline-flex items-center font-body text-xs font-medium tracking-[0.18em] uppercase transition-colors hover:text-brand-500 ${colorClass} ${className}`}
            aria-label={`Switch to ${other.toUpperCase()}`}
            data-testid="language-toggle"
        >
            <span className={lang === "de" ? "text-brand-500" : "opacity-60"}>DE</span>
            <span className="mx-1.5 opacity-30">|</span>
            <span className={lang === "en" ? "text-brand-500" : "opacity-60"}>EN</span>
        </button>
    );
};
